import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Paper,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Select,
  InputAdornment,
  TablePagination,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { getAdminAuditLog } from "./roleAndPermissionActions";
import { RootState } from "../../store/store";
import { IAuditLogEntry } from "./roleAndPermissionType";
import RoleAndPermissionStyles from "./RoleAndPermissionStyles";

const DROPDOWN_WIDTH = 240;

const AuditLog = () => {
  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("All");
  const [auditLogs, setAuditLogs] = useState<IAuditLogEntry[] | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const dispatch = useAppDispatch();
  const { auditLogData } = useAppSelector(
    (state: RootState) => state.adminRoles
  );

  useEffect(() => {
    if (auditLogData?.rolesAndPermissionsAuditLogs?.length) {
      setAuditLogs(auditLogData?.rolesAndPermissionsAuditLogs);
    }
  }, [auditLogData?.rolesAndPermissionsAuditLogs]);

  const getUniqueActions = (logs: typeof auditLogs) => {
    const actions = logs && logs.map((log) => log.action);
    return ["All", ...Array.from(new Set(actions))];
  };

  const actions = useMemo(
    () => getUniqueActions(auditLogs && auditLogs),
    [auditLogs]
  );

  const filteredLogs = useMemo(
    () =>
      auditLogs &&
      auditLogs.filter(
        (log) =>
          (actionFilter === "All" || log.action === actionFilter) &&
          (log.action.toLowerCase().includes(search.toLowerCase()) ||
            log.user.toLowerCase().includes(search.toLowerCase()) ||
            log.details.toLowerCase().includes(search.toLowerCase()) ||
            log.timeStamp.toLowerCase().includes(search.toLowerCase()))
      ),
    [search, actionFilter, auditLogs]
  );

  const paginatedLogs = useMemo(
    () =>
      filteredLogs
        ? filteredLogs.slice(
            page * rowsPerPage,
            page * rowsPerPage + rowsPerPage
          )
        : [],
    [filteredLogs, page, rowsPerPage]
  );

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    dispatch(getAdminAuditLog());
  }, []);
  return (
    <Box
      id="auditlog_container"
      data-test-id="test-auditlog_container"
      aria-label="Audit Log Container"
    >
      <Box
        sx={RoleAndPermissionStyles.auditLogSearchRow}
        id="auditlog_search_row"
        data-test-id="test-auditlog_search_row"
        aria-label="Audit Log Search and Filter Row"
      >
        <TextField
          placeholder="Search audit logs..."
          size="small"
          sx={RoleAndPermissionStyles.auditLogSearchField}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          id="auditlog_search_input"
          data-test-id="test-auditlog_search_input"
          aria-label="Search Audit Logs Input"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#A0AEC0" }} />
              </InputAdornment>
            ),
            sx: RoleAndPermissionStyles.auditLogInputProps,
          }}
        />
        <Box
          sx={RoleAndPermissionStyles.auditLogDropdownBox}
          id="auditlog_filter_box"
          data-test-id="test-auditlog_filter_box"
          aria-label="Audit Log Action Filter Box"
        >
          <Select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            size="small"
            sx={RoleAndPermissionStyles.auditLogSelect}
            displayEmpty
            id="auditlog_action_filter"
            data-test-id="test-auditlog_action_filter"
            aria-label="Filter Audit Logs by Action"
            MenuProps={{
              PaperProps: {
                sx: RoleAndPermissionStyles.auditLogMenuPaper,
                anchorOrigin: { vertical: "bottom", horizontal: "left" },
                transformOrigin: { vertical: "top", horizontal: "left" },
              },
              anchorOrigin: { vertical: "bottom", horizontal: "left" },
              transformOrigin: { vertical: "top", horizontal: "left" },
            }}
          >
            {actions &&
              actions.map((action) => (
                <MenuItem
                  key={action}
                  value={action}
                  id={`auditlog_action_option_${action}`}
                  data-test-id={`test-auditlog_action_option_${action}`}
                  aria-label={`Filter Option ${action}`}
                >
                  {action}
                </MenuItem>
              ))}
          </Select>
        </Box>
      </Box>

      <TableContainer
        component={Paper}
        id="auditlog_table_container"
        data-test-id="test-auditlog_table_container"
        aria-label="Audit Log Table Container"
      >
        <Table
          id="auditlog_table"
          data-test-id="test-auditlog_table"
          aria-label="Audit Log Table"
        >
          <TableHead>
            <TableRow>
              <TableCell id="auditlog_header_action">Action</TableCell>
              <TableCell id="auditlog_header_user">User</TableCell>
              <TableCell id="auditlog_header_details">Details</TableCell>
              <TableCell id="auditlog_header_timestamp">Timestamp</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedLogs && paginatedLogs.length === 0 ? (
              <TableRow
                id="auditlog_no_results_row"
                data-test-id="test-auditlog_no_results_row"
              >
                <TableCell
                  colSpan={4}
                  align="center"
                  aria-label="No Audit Logs Found"
                >
                  No audit logs found.
                </TableCell>
              </TableRow>
            ) : (
              paginatedLogs &&
              paginatedLogs?.map((log, idx) => (
                <TableRow
                  key={idx + page * rowsPerPage}
                  id={`auditlog_row_${idx + page * rowsPerPage}`}
                  data-test-id={`test-auditlog_row_${idx + page * rowsPerPage}`}
                  aria-label={`Audit Log Row ${idx + page * rowsPerPage}`}
                >
                  <TableCell
                    id={`auditlog_action_${idx + page * rowsPerPage}`}
                    data-test-id={`test-auditlog_action_${
                      idx + page * rowsPerPage
                    }`}
                  >
                    {log.action}
                  </TableCell>
                  <TableCell
                    id={`auditlog_user_${idx + page * rowsPerPage}`}
                    data-test-id={`test-auditlog_user_${
                      idx + page * rowsPerPage
                    }`}
                  >
                    {log.user}
                  </TableCell>
                  <TableCell
                    id={`auditlog_details_${idx + page * rowsPerPage}`}
                    data-test-id={`test-auditlog_details_${
                      idx + page * rowsPerPage
                    }`}
                  >
                    {log.details}
                  </TableCell>
                  <TableCell
                    id={`auditlog_timestamp_${idx + page * rowsPerPage}`}
                    data-test-id={`test-auditlog_timestamp_${
                      idx + page * rowsPerPage
                    }`}
                  >
                    {new Date(log.timeStamp).toLocaleString()
                      ? new Date(log.timeStamp).toLocaleString()
                      : log.timeStamp}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredLogs ? filteredLogs.length : 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          id="auditlog_table_pagination"
          data-test-id="test-auditlog_table_pagination"
          aria-label="Audit Log Table Pagination"
        />
      </TableContainer>
    </Box>
  );
};

export default AuditLog;
